<template>
  <span
    type="file"
    data-drag-handle
    draggable="true"
    class="drag-handler relative"
  >
    <i
      v-if="editor.isEditable"
      data-drag-handle
      draggable="true"
      class="icon-drag_indicator drag-icon pr-4 absolute mt-15"
    />
    <span
      class="bg-sidebar text-14 p-8 my-5 rounded-4 text-app-color inline-flex items-center max-w-full"
      :class="status > 0 ? 'opacity-50': 'hover:cursor-pointer hover:opacity-80'"
      @click.stop="download"
    >
      <i class="icon-attach-file text-22 mr-5" />
      <span style="font-size: 0.875rem" class="truncate">{{ node.attrs.name }}</span>
      <span class="ml-10 text-grey-250" style="font-size: 0.75rem">{{ size }}</span>
      <ButtonModule
        v-if="editor.isEditable"
        prefix="icon-close"
        :has-hover="true"
        class="text-app-color cursor-pointer ml-5"
        button-opacity="80"
        @click.stop="cancelUpload"
      />
    </span>
    <span
      v-if="status > 0"
      class="block relative w-full max-w-250 h-20  rounded-8 overflow-hidden mb-10 border-2"
      :class="isHueTheme ? 'border-link-color' : 'border-main-color'"
    >
      <span
        class="block h-20 transition-all duration-300"
        :class="isHueTheme ? 'bg-link-color' : 'bg-main-color'"
        :style="`width: ${ status }%`"
      >
        <span class="absolute top-0 transform -translate-x-1/2 left-1/2 leading-normal" style="font-size: 12px">{{ status }}%</span>
      </span>
    </span>
  </span>
</template>

<script>
import { computed, defineComponent, onBeforeUpdate, ref, watch, onMounted } from 'vue'
import { attachmentService } from '@/services'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'

export default defineComponent({
  components: { ButtonModule },

  props: {
    node: { type: Object },
    editor: { type: Object },
    updateAttributes: { type: Function },
    getPos: { type: Function }
  },

  setup (props) {
    const { state, commit, getters } = useStore()
    const route = useRoute()

    const isHueTheme = computed(() => getters.isHueTheme)

    const status = ref(props.node.attrs.percent)
    const currentProject = computed(() => state.project.currentProject)
    const isSharePage = computed(() => route.name === 'Share')
    const share = computed(() => state.project.share)
    const files = computed(() => state.file.files)
    const file = computed(() => files.value[props.node.attrs.id])
    const isTaskBoardPage = computed(() => route.name === 'TaskBoard' || route.name === 'TaskBoardTeam')
    const isDiscussions = computed(() => route.name === 'Discussions' || route.name === 'Discussion')

    let allowUpdate = true

    watch(files, value => {
      setTimeout(() => {
        if ((isSharePage.value || +route.query.page === currentProject.value?.id || isTaskBoardPage.value || isDiscussions.value) && allowUpdate) {
          props.updateAttributes({ percent: value[props.node.attrs.id]?.percent || -1 }, props.getPos())
        }
      }, 50)
    }, {
      deep: true
    })

    onBeforeUpdate(() => {
      status.value = props.node.attrs.percent
    })

    onMounted(() => {
      const oldStatus = status.value

      setTimeout(() => {
        /* eslint-disable-next-line */
        if (oldStatus === status.value && status.value != -1 && oldStatus != -1) {
          deleteFile()
        }
      }, 3000)
    })

    const size = computed(() => {
      const kb = (props.node.attrs.size / 1024).toFixed(2)
      const mb = (props.node.attrs.size / 1024 / 1024).toFixed(2) + 'MB'

      return kb > 1000 ? mb : kb + 'KB'
    })

    function cancelUpload () {
      if (status.value !== -1 && file.value?.abort) {
        allowUpdate = false
        file.value.abort()
      }

      deleteFile()
    }

    function deleteFile () {
      setTimeout(() => commit('deleteFile', props.node.attrs.id), 100)
      props.editor.commands.deleteImage(props.getPos())
    }

    async function download () {
      if (props.node.attrs.percent >= 0) return

      const methodName = isSharePage.value ? 'getShareFile' : 'getFile'

      const { data } = await attachmentService[methodName]({
        id: props.node.attrs.id,
        projectId: props.node.attrs.projectId || currentProject.value?.id,
        discussionId: props.node.attrs?.discussionId,
        taskId: props.node.attrs?.taskId,
        shareId: share.value?.id
      })

      if (!data.url) {
        deleteFile()

        return commit('setNotification', {
          isVisible: true,
          message: 'File was not uploaded'
        })
      }

      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = data.url
      a.target = '_blank'
      a.download = props.node.attrs.name
      document.body.append(a)
      a.click()
      document.body.removeChild(a)
    }

    return {
      size,
      status,
      isHueTheme,
      download,
      cancelUpload
    }
  }
})
</script>
